// ============================================================================
// CrewAhead Maritime Platform - Auth API Functions
// ============================================================================

import axios from 'axios';
import { apiClient, setTokens, clearAuth, handleApiError, CrewAheadApiError } from './client';
import { API_ENDPOINTS, STORAGE_KEYS } from '../utils/constants';
import type {
  AuthResponse,
  ChangePasswordData,
  ForgotPasswordData,
  LoginCredentials,
  RefreshTokenResponse,
  RegisterData,
  ResetPasswordData,
  ResendVerificationData,
  User,
  VerifyEmailData,
  VerifyEmailResponse,
} from '../types';

type SocialSignupRole = RegisterData['role'];

// ─── Auth API ────────────────────────────────────────────────────────────────

/**
 * Authenticates a user with email and password.
 *
 * @param credentials - Login credentials (email + password)
 * @returns Auth response with user data and tokens
 */
export async function login(credentials: LoginCredentials): Promise<AuthResponse> {
  try {
    const response = await apiClient.post<AuthResponse>(
      API_ENDPOINTS.AUTH.LOGIN,
      credentials
    );

    const authData = response.data;
    setTokens(authData.accessToken, authData.refreshToken);
    return authData;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * Registers a new user account.
 *
 * @param data - Registration data including email, password, name, role
 * @returns Auth response with user data and tokens
 */
export async function register(data: RegisterData): Promise<AuthResponse> {
  try {
    const response = await apiClient.post<AuthResponse>(
      API_ENDPOINTS.AUTH.REGISTER,
      data
    );

    const authData = response.data;
    setTokens(authData.accessToken, authData.refreshToken);
    return authData;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * Logs out the current user and clears all auth state.
 */
export async function logout(): Promise<void> {
  try {
    await apiClient.post<void>(API_ENDPOINTS.AUTH.LOGOUT);
  } catch (error) {
    // Even if the server request fails, clear local auth
    console.warn('Logout API call failed, clearing local auth state:', error);
  } finally {
    clearAuth();
  }
}

/**
 * Refreshes the access token using the stored refresh token.
 *
 * @returns New access token and refresh token pair
 */
export async function refreshToken(): Promise<RefreshTokenResponse> {
  try {
    if (typeof window === 'undefined') {
      throw new CrewAheadApiError({
        statusCode: 401,
        message: 'Cannot refresh token on server',
        code: 'SERVER_SIDE_REFRESH',
        details: null,
        timestamp: new Date().toISOString(),
      });
    }

    const refreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);

    if (!refreshToken) {
      throw new CrewAheadApiError({
        statusCode: 401,
        message: 'No refresh token available',
        code: 'NO_REFRESH_TOKEN',
        details: null,
        timestamp: new Date().toISOString(),
      });
    }

    const response = await apiClient.post<RefreshTokenResponse>(
      API_ENDPOINTS.AUTH.REFRESH_TOKEN,
      { refresh: refreshToken }
    );

    const tokenData = response.data;
    setTokens(tokenData.accessToken, tokenData.refreshToken);
    return tokenData;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * Verifies a user's email address using the verification token.
 *
 * @param token - Email verification token from the email link
 * @returns Detail message plus a code: "verified" on first success,
 *          "already_verified" when the address was already confirmed
 */
export async function verifyEmail(token: VerifyEmailData['token']): Promise<VerifyEmailResponse> {
  try {
    const response = await apiClient.post<VerifyEmailResponse>(
      API_ENDPOINTS.AUTH.VERIFY_EMAIL,
      {
        token,
      }
    );
    return response.data;
  } catch (error) {
    const wrapped = handleApiError(error);
    // The backend distinguishes invalid ("invalid_token") from expired
    // ("expired_token") tokens via a code field in the 400 body. Surface it
    // on the typed error so callers can branch without parsing messages.
    const backendCode = axios.isAxiosError(error)
      ? (error.response?.data as { code?: string } | undefined)?.code
      : undefined;
    if (backendCode) {
      throw new CrewAheadApiError({
        message: wrapped.message,
        statusCode: wrapped.statusCode,
        code: backendCode,
        details: wrapped.details,
        timestamp: wrapped.timestamp,
      });
    }
    throw wrapped;
  }
}

/**
 * Requests a new email verification link.
 *
 * The endpoint is non-revealing and throttled: it returns the same generic
 * response whether or not the address exists, and 429s after the per-IP
 * limit is hit.
 *
 * @param data - Object containing the email address to re-verify
 */
export async function resendVerification(data: ResendVerificationData): Promise<{ detail: string }> {
  try {
    const response = await apiClient.post<{ detail: string }>(
      API_ENDPOINTS.AUTH.RESEND_VERIFICATION,
      data
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * Requests a password reset email.
 *
 * @param data - Object containing the user's email
 */
export async function forgotPassword(data: ForgotPasswordData): Promise<void> {
  try {
    await apiClient.post<void>(
      API_ENDPOINTS.AUTH.FORGOT_PASSWORD,
      data
    );
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * Resets a user's password using the reset token.
 *
 * @param data - Reset token and new password
 */
export async function resetPassword(data: ResetPasswordData): Promise<void> {
  try {
    await apiClient.post<void>(
      API_ENDPOINTS.AUTH.RESET_PASSWORD,
      data
    );
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * Changes the current user's password.
 *
 * @param data - Current password and new password
 */
export async function changePassword(data: ChangePasswordData): Promise<void> {
  try {
    await apiClient.post<void>(
      API_ENDPOINTS.AUTH.CHANGE_PASSWORD,
      data
    );
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * Requests soft-deletion of the current user's account.
 * Requires the current password. The account enters a 30-day grace period.
 */
export async function deleteAccount(password: string): Promise<{ detail: string }> {
  try {
    const response = await apiClient.delete<{ detail: string }>(
      "/auth/account",
      { data: { password } }
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * Cancels a pending account deletion within the 30-day grace period.
 * Public endpoint — requires the account's email and password.
 */
export async function cancelAccountDeletion(data: {
  email: string;
  password: string;
}): Promise<{ detail: string }> {
  try {
    const response = await apiClient.post<{ detail: string }>(
      "/auth/account/cancel-deletion",
      data
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * Fetches the currently authenticated user's data.
 *
 * @returns Current user object
 */
export async function getCurrentUser(): Promise<User> {
  try {
    const response = await apiClient.get<User>(
      API_ENDPOINTS.AUTH.ME
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * Updates the current user's account info (name, phone, nationality, DOB).
 */
export async function updateAccountInfo(data: {
  firstName?: string;
  lastName?: string;
  phone?: string;
  nationality?: string;
  nationalityCountryId?: number | null;
  dateOfBirth?: string;
  countryOfResidence?: string;
  countryOfResidenceCountryId?: number | null;
  city?: string;
  cityCountryId?: number | null;
}): Promise<User> {
  try {
    const response = await apiClient.patch<User>('/auth/profile', data);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

// ─── Email Change (staged, verified via link to the new address) ─────────────

export interface PendingEmailChange {
  newEmail: string;
  createdAt: string;
  expiresAt: string;
}

/**
 * Fetch the current pending (unconfirmed, unexpired) email change, if any.
 */
export async function getPendingEmailChange(): Promise<PendingEmailChange | null> {
  try {
    const response = await apiClient.get<{ pending: PendingEmailChange | null }>(
      '/auth/email/change-request'
    );
    return response.data.pending;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * Stage an email change. Sends a confirmation link to the NEW address; the
 * login email only swaps once the link is confirmed.
 */
export async function requestEmailChange(data: {
  newEmail: string;
  password: string;
}): Promise<{ detail: string; pending: PendingEmailChange }> {
  try {
    const response = await apiClient.post<{ detail: string; pending: PendingEmailChange }>(
      '/auth/email/change-request',
      data
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * Confirm a staged email change via the token from the confirmation email.
 * Public endpoint — the token is the proof.
 */
export async function confirmEmailChange(token: string): Promise<{ detail: string; email: string }> {
  try {
    const response = await apiClient.post<{ detail: string; email: string }>(
      '/auth/email/confirm-change',
      { token }
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

// ─── Social Authentication ───────────────────────────────────────────────────

/**
 * Exchange Google id_token for JWT tokens.
 *
 * @param idToken - Google ID token from the frontend OneTap/SignInWithGoogle
 * @returns Auth response with user data and tokens (same shape as login)
 */
export async function googleAuth(idToken: string, role?: SocialSignupRole) {
  try {
    const response = await apiClient.post(
      API_ENDPOINTS.AUTH.SOCIAL_GOOGLE,
      { idToken, role }
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * Exchange Facebook access_token for JWT tokens.
 *
 * @param accessToken - Facebook access token from FB.login()
 * @returns Auth response with user data and tokens (same shape as login)
 */
export async function facebookAuth(accessToken: string, role?: SocialSignupRole) {
  try {
    const response = await apiClient.post(
      API_ENDPOINTS.AUTH.SOCIAL_FACEBOOK,
      { accessToken, role }
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * Exchange LinkedIn authorization code for JWT tokens.
 *
 * @param code - Authorization code from LinkedIn OAuth redirect
 * @param redirectUri - Must match the redirect_uri used in the authorize request
 * @returns Auth response with user data and tokens (same shape as login)
 */
export async function linkedInAuth(code: string, redirectUri: string, role?: SocialSignupRole) {
  try {
    const response = await apiClient.post(
      API_ENDPOINTS.AUTH.SOCIAL_LINKEDIN,
      { code, redirectUri, role }
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}
